import { persistentAtom, setPersistentEngine } from '@nanostores/persistent';
import { computed, onMount, onSet } from 'nanostores';
import easyfyHttpClient from '@shared/easyfy.httpclient';

export interface FavoriteProduct {
  productId: string;
  sku?: string;
  salesUnit: { unitCode: string; unitPackSize: number } | null;
}

function hasLocalStorage(): boolean {
  try {
    const probeKey = '__easyfy_local_storage_probe__';
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

// @nanostores/persistent only checks `typeof localStorage !== 'undefined'`, so a null
// localStorage (e.g. Android WebView without DOM storage) crashes persistentAtom on load.
if (!hasLocalStorage()) {
  setPersistentEngine({}, { addEventListener() {}, removeEventListener() {} });
}

const prefixPath = document.documentElement.dataset.prefixPath?.replace(/\//g, '');
const storageKey = prefixPath ? `favourite-products-${prefixPath}` : 'favourite-products';

export const $favourites = persistentAtom<FavoriteProduct[]>(storageKey, [], {
  encode: JSON.stringify,
  decode: (raw) => {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.every((i) => typeof i?.productId === 'string')
        ? (parsed as FavoriteProduct[])
        : [];
    } catch {
      return [];
    }
  },
});

export const $favouriteIds = computed($favourites, (list) => list.map((p) => p.productId));
export const $favouriteSkus = computed($favourites, (list) => list.map((p) => p.sku).filter((sku): sku is string => !!sku));
export const $favouriteCount = computed($favourites, (list) => list.length);

// Prevents the auto-POST listener from re-POSTing changes written from a server response.
let suppress = false;

function setSilently(next: FavoriteProduct[]): void {
  if (syncInFlight) {
    // Only the removals are recorded. Retained entries stay unrecorded so the sync response
    // can still enrich them with the SKUs it carries.
    const nextIds = new Set(next.map((p) => p.productId));
    for (const p of $favourites.get()) {
      if (!nextIds.has(p.productId)) writesDuringSync.set(p.productId, 'dropped');
    }
  }
  suppress = true;
  try {
    $favourites.set(next);
  } finally {
    suppress = false;
  }
}

// Resolves once the page-load sync has settled (success, no-op, or failure), so consumers
// can read the SKU-enriched list before pushing page-load analytics. It only settles on a
// page that uses the store: subscribe to it, read it, or call ensureFavouritesSynced().
let resolveInitialSync: () => void;
export const initialSyncComplete = new Promise<void>((resolve) => {
  resolveInitialSync = resolve;
});

let syncStarted = false;

// Merges the local list with the server's authoritative list. Repeat calls are no-ops
export function ensureFavouritesSynced(): Promise<void> {
  if (!syncStarted) {
    syncStarted = true;
    syncWithServer()
      .catch(() => {})
      .finally(() => resolveInitialSync());
  }
  return initialSyncComplete;
}

onMount($favourites, () => {
  void ensureFavouritesSynced();
});

// Writes made while a sync is in flight, so the response can be rebased onto them.
// 'removed' means the product was removed locally and a delete delta went out with it.
// 'dropped' means it left the local cache without the server list being touched.
type InFlightWrite = FavoriteProduct | 'removed' | 'dropped';

let syncInFlight = false;
const writesDuringSync = new Map<string, InFlightWrite>();

export async function syncWithServer(): Promise<void> {
  const products = $favourites.get();
  writesDuringSync.clear();
  syncInFlight = true;

  let data: { success?: boolean; products?: unknown } | undefined;
  try {
    const result = await easyfyHttpClient.post(
      '/api/ordertemplate?handler=syncfavourites',
      { products },
      ''
    );
    data = result?.data;
  } finally {
    syncInFlight = false;
  }

  if (!data?.success || !Array.isArray(data.products)) return;

  const server = data.products as FavoriteProduct[];
  const rebased: FavoriteProduct[] = [];

  for (const p of server) {
    const local = writesDuringSync.get(p.productId);
    if (local === 'dropped') continue;
    if (local === 'removed') {
      // The server merge is a union of the posted snapshot, so a product removed while the
      // request was open is in the response, and on the server again. Re-issue the delta.
      easyfyHttpClient.post('/api/ordertemplate?handler=removefromfavourites', { productId: p.productId }, '');
      continue;
    }
    rebased.push(local ?? p);
  }

  const present = new Set(rebased.map((p) => p.productId));
  for (const [productId, local] of writesDuringSync) {
    if (typeof local === 'object' && !present.has(productId)) rebased.push(local);
  }

  setSilently(rebased);
}

// Auto-POSTs add/remove/salesUnit deltas on every local write. onSet rather than listen()
// because a listener counts as a subscriber and would mount the store on import; it also
// skips cross-tab storage events, which write through the raw setter.
onSet($favourites, ({ newValue: next }) => {
  if (suppress) return;

  const previous = $favourites.value ?? [];
  const prevIds = new Set(previous.map((p) => p.productId));
  const nextIds = new Set(next.map((p) => p.productId));

  for (const p of next) {
    if (!prevIds.has(p.productId)) {
      easyfyHttpClient.post('/api/ordertemplate?handler=addtofavourites', { productId: p.productId, salesUnit: p.salesUnit }, '');
      if (syncInFlight) writesDuringSync.set(p.productId, p);
    } else {
      const prev = previous.find((x) => x.productId === p.productId);
      if (prev && prev.salesUnit?.unitCode !== p.salesUnit?.unitCode) {
        easyfyHttpClient.post('/api/ordertemplate?handler=updatefavouritesalesunit', { productId: p.productId, salesUnit: p.salesUnit }, '');
        if (syncInFlight) writesDuringSync.set(p.productId, p);
      }
    }
  }
  for (const p of previous) {
    if (!nextIds.has(p.productId)) {
      easyfyHttpClient.post('/api/ordertemplate?handler=removefromfavourites', { productId: p.productId }, '');
      if (syncInFlight) writesDuringSync.set(p.productId, 'removed');
    }
  }
});

export function addFavourite(productId: string, sku: string | undefined, salesUnit: FavoriteProduct['salesUnit']): void {
  if ($favourites.get().some((p: FavoriteProduct) => p.productId === productId)) return;
  $favourites.set([...$favourites.get(), { productId, sku, salesUnit }]);
}

export function removeFavourite(productId: string): void {
  $favourites.set($favourites.get().filter((p: FavoriteProduct) => p.productId !== productId));
}

export function updateSalesUnit(productId: string, salesUnit: FavoriteProduct['salesUnit']): void {
  $favourites.set(
    $favourites.get().map((p: FavoriteProduct) => (p.productId === productId ? { ...p, salesUnit } : p))
  );
}

// Drops the local cache only. The server list is untouched, so this must not emit.
// remove deltas for everything it discards.
export function clearFavourites(): void {
  setSilently([]);
}

// Fetches the server-rendered flyout HTML. For unauthenticated users the local
// product list is forwarded so the partial can still be rendered client-side.
export async function fetchFlyoutHtml(): Promise<string | null> {
  const result = await easyfyHttpClient.post(
    '/api/ordertemplate?handler=fetchfavourites',
    { products: $favourites.get() },
    ''
  );
  const data = result?.data;
  if (data?.success) {
    if (Array.isArray(data.productIds)) {
      const found = new Set<string>(data.productIds as string[]);
      const current = $favourites.get();
      const pruned = current.filter((p) => found.has(p.productId));
      if (pruned.length !== current.length) setSilently(pruned);
    }
    return data.partialHtml ?? null;
  }
  return null;
}
